import React from 'react';
import { useTranslation } from 'react-i18next';
import { HomeTaskAttachments } from '@/components/HomeTaskModal/HomeTaskAttachments';
import { Paperclip } from '@/components/Icons/Paperclip';
import { TestPaper } from '@/components/Icons/TestPaper';
import type { Attachment } from '@/types/attachment';
import type { ScheduledTest } from '@/types/scheduledTests';
import { sanitizeRichTextHtml } from '@/utils/html';
import { isBlank } from '@/utils/text';

interface Props {
  test: ScheduledTest;
  onPreview: (attachment: Attachment) => void;
}

export const ScheduledTestCard: React.FC<Props> = ({ test, onPreview }) => {
  const { t } = useTranslation();
  const { discipline, description } = test;
  const hasDescription = !isBlank(description.text);
  const attachments = description.attachments ?? [];

  return (
    <div className="mx-4 mb-2 flex flex-col rounded-xl bg-card p-4 shadow-card md:mx-5">
      <div className="flex items-center gap-6">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <span className="bg-info-surface text-primary flex size-8 shrink-0 items-center justify-center rounded-full">
            <TestPaper className="size-4" />
          </span>
          <h4 className="text-subhead-14 truncate text-foreground">
            {discipline.name}
          </h4>
        </div>

        {attachments.length > 0 && (
          <span className="bg-info-surface text-primary flex shrink-0 items-center gap-1 rounded-xl py-0.5 pr-2 pl-1">
            <Paperclip className="size-4" />
            <span className="text-caption-12">{attachments.length}</span>
          </span>
        )}
      </div>

      <div className="border-divider my-3 border-t" />

      {hasDescription && (
        <div
          className="rich-text text-body-14 text-muted-foreground"
          dangerouslySetInnerHTML={{
            __html: sanitizeRichTextHtml(description.text),
          }}
        />
      )}

      {!hasDescription && (
        <p className="text-body-14 text-muted-foreground">
          {t('diary.emptyDescription')}
        </p>
      )}

      {attachments.length > 0 && (
        <div className="mt-3">
          <HomeTaskAttachments
            attachments={attachments}
            isColumnOnMobile
            onPreview={onPreview}
          />
        </div>
      )}
    </div>
  );
};
